import React, {useContext, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ITEM_ROUTE, ROLE_ADMIN} from '../../util/Constants';
import {observer} from 'mobx-react-lite';
import {Context} from '../../index';
import ItemUpdate from '../admin/ItemUpdate';
import {itemImageSrc} from '../../util/imageUrl';
import {addToGuestCart, readGuestCart} from '../../util/guestCart';
import {addItemToCart, deleteItemFromCart} from '../../http/cartAPI';
import {catalogItemId, hasCatalogItemId} from '../../util/catalogItemId';
import {effectiveStockCap} from '../../util/cartLineAvailable';

const PLACEHOLDER = 'https://placehold.co/240x240/f7f4ed/284a38?text=Lutica';

function notifyCartPages() {
    window.dispatchEvent(new CustomEvent('lutica-cart-changed'));
}

const ItemCard = observer(({item}) => {
    const {user, cart} = useContext(Context);
    const {title, price, count} = item;
    const [itemForm, setItemForm] = useState(false);
    const [cartError, setCartError] = useState('');
    const [plusWarn, setPlusWarn] = useState(false);
    const plusWarnTimerRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const src = itemImageSrc(item, 0) || PLACEHOLDER;
    const pid = catalogItemId(item);
    const pidOk = hasCatalogItemId(item);
    const pidNum = Number(pid);
    const inCart = pidOk ? cart.qtyForItem(pid) : 0;
    const cartLine = pidOk ? cart.lines.find((l) => Number(l.itemId ?? l.id) === pidNum) : null;
    const maxStock = effectiveStockCap(count, cartLine);

    const flashPlusLimit = () => {
        setPlusWarn(true);
        if (plusWarnTimerRef.current) {
            window.clearTimeout(plusWarnTimerRef.current);
        }
        plusWarnTimerRef.current = window.setTimeout(() => setPlusWarn(false), 700);
    };

    const openItem = () => {
        if (!pidOk) return;
        navigate(`${ITEM_ROUTE}/${pid}`, {state: {background: location}});
    };

    const addOne = (e) => {
        e.stopPropagation();
        if (!pidOk || maxStock <= 0) return;
        if (inCart >= maxStock) {
            flashPlusLimit();
            return;
        }
        setCartError('');
        if (!user.isAuth) {
            addToGuestCart(item, 1);
            cart.setTotalFromLines(readGuestCart());
            notifyCartPages();
            return;
        }
        addItemToCart(pid)
            .then(() => cart.refreshTotals(true))
            .then(() => notifyCartPages())
            .catch(() => {
                flashPlusLimit();
            });
    };

    const removeOne = (e) => {
        e.stopPropagation();
        if (!pidOk || inCart <= 0) return;
        setCartError('');
        if (!user.isAuth) {
            addToGuestCart(item, -1);
            cart.setTotalFromLines(readGuestCart());
            notifyCartPages();
            return;
        }
        deleteItemFromCart(pid)
            .then(() => cart.refreshTotals(true))
            .then(() => notifyCartPages())
            .catch(() => setCartError('Не удалось изменить корзину.'));
    };

    return (
        <div className="flex w-full min-w-0 max-w-full flex-col">
            <div
                className="group relative flex flex-col overflow-hidden rounded-3xl border border-stone-200/70 bg-cream-100 shadow-sm transition duration-200 hover:border-pine-300/80 hover:shadow-lg hover:ring-2 hover:ring-pine-400/50 focus-within:ring-2 focus-within:ring-pine-500"
            >
                <button
                    type="button"
                    className="relative block aspect-square w-full cursor-pointer overflow-hidden rounded-t-3xl rounded-b-none bg-cream-200/50 text-left outline-none"
                    onClick={openItem}
                    disabled={!pidOk}
                    aria-label={pidOk ? `${title}. Подробнее о товаре` : `${title}. Карточка без идентификатора`}
                >
                    <img
                        src={src}
                        alt=""
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = PLACEHOLDER;
                        }}
                    />
                    <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[22%] min-h-[3.75rem] max-h-[5.25rem] bg-gradient-to-t from-bark-900/90 via-bark-900/55 to-transparent sm:h-[20%] sm:min-h-[4rem] sm:max-h-[5.75rem]"
                        aria-hidden
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] p-2.5 pb-2 text-left text-white sm:p-3 sm:pb-2.5">
                        <p className="text-sm font-semibold leading-snug sm:text-base sm:leading-tight">{title}</p>
                        <p className="mt-0.5 text-xs text-cream-100/90 sm:mt-1 sm:text-sm">{price} ₽</p>
                    </div>
                </button>

                <div className="border-t border-stone-200/80 bg-cream-50/95 p-2">
                    {maxStock > 0 ? (
                        inCart > 0 ? (
                            <div className="flex h-10 w-full items-center justify-center gap-2">
                                <button
                                    type="button"
                                    onClick={removeOne}
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-stone-200/90 text-lg font-semibold leading-none text-bark-700 hover:bg-cream-100 active:bg-cream-200 touch-manipulation"
                                    aria-label="Убрать одну штуку"
                                >
                                    −
                                </button>
                                <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums text-bark-900">
                                    {inCart}
                                </span>
                                <button
                                    type="button"
                                    onClick={addOne}
                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-semibold leading-none transition-colors touch-manipulation ${
                                        plusWarn
                                            ? 'border-0 bg-red-600 text-white ring-2 ring-red-400 animate-pulse'
                                            : inCart >= maxStock
                                              ? 'border border-stone-200 bg-stone-200 text-bark-300'
                                              : 'border-0 bg-pine-700 text-white hover:bg-pine-800 active:bg-pine-900'
                                    }`}
                                    aria-label="Добавить ещё одну"
                                >
                                    +
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={addOne}
                                className="lut-btn-pine flex h-10 w-full items-center justify-center text-sm font-semibold shadow-md shadow-pine-900/10 touch-manipulation"
                            >
                                В корзину
                            </button>
                        )
                    ) : (
                        <span className="flex h-10 w-full items-center justify-center rounded-2xl border border-stone-200 bg-stone-50 text-sm text-bark-400">
                            Нет в наличии
                        </span>
                    )}
                </div>
            </div>
            {user.userRole === ROLE_ADMIN && (
                <div className="mt-3 flex flex-wrap justify-center gap-2 px-0.5">
                    <button type="button" onClick={() => setItemForm(true)} className="lut-btn-danger text-sm">
                        Изменить
                    </button>
                    <ItemUpdate oldItem={item} show={itemForm} onHide={() => setItemForm(false)}/>
                </div>
            )}
            {cartError ? <p className="mt-2 px-0.5 text-center text-xs text-red-600">{cartError}</p> : null}
        </div>
    );
});

export default ItemCard;
